import RepairBoardViewpoint from '../components/tv/RepairBoardViewpoint';
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Maximize2, Minimize2, X, Target, ChevronDown, Truck, Package, Tv, Repeat, Check, Users, ShieldCheck, BadgeCheck, Megaphone, AlertTriangle, HardHat, PartyPopper, Wrench } from 'lucide-react';

const PERIODS = ['daily', 'weekly', 'monthly'];
const REFRESH_MS = 60_000;
const STORAGE_KEY = 'app:tv_viewpoint';
const ROTATION_KEY = 'app:tv_rotation';
const PT_FILTER_KEY = 'app:tv_pt_filter';

const VIEWPOINTS = [
  { key: 'production', label: 'Production Floor', icon: Target, description: 'Production progress vs. target' },
  { key: 'open_orders', label: 'Open Orders', icon: Truck, description: 'Items on order — pending, overdue, top vendors' },
  { key: 'inventory_pulse', label: 'Inventory Pulse', icon: Package, description: 'Low stock + receiving + recent activity' },
  { key: 'on_shift', label: 'On Shift Now', icon: Users, description: 'Who is clocked in right now' },
  { key: 'quality_pulse', label: 'Quality Pulse', icon: ShieldCheck, description: 'QC pass rate · top failures' },
  { key: 'finished_today', label: 'Finished Today', icon: BadgeCheck, description: 'Units built today + this week' },
  { key: 'repair_board', label: 'Repair Board', icon: Wrench, description: 'Machines in repair by status' },
  { key: 'delivery_schedule', label: 'Delivery Schedule', icon: Truck, description: 'Issued BoLs · drafts · top consignees' },
  { key: 'active_recalls', label: 'Active Recalls', icon: AlertTriangle, description: 'Open recalls + affected units' },
  { key: 'safety', label: 'Safety', icon: HardHat, description: 'Days without incident' },
  { key: 'celebrations', label: 'Celebrations', icon: PartyPopper, description: 'Birthdays · work anniversaries' },
  { key: 'company_news', label: 'Company News', icon: Megaphone, description: 'Announcements from leadership' },
];

const ROTATE_INTERVALS = [
  { value: 15, label: '15s' }, { value: 30, label: '30s' },
  { value: 60, label: '1m' }, { value: 120, label: '2m' }, { value: 300, label: '5m' },
];

// iter61 — TV realtime: each viewpoint can subscribe to backend push events.
// The top-level TvViewPage opens the WS once and re-broadcasts to a window event;
// each viewpoint hook listens for the event types it cares about and re-fetches.
const useTvRealtime = (fetchData, eventTypes) => {
  useEffect(() => {
    if (!fetchData) return undefined;
    const handler = (ev) => {
      const t = ev.detail?.type;
      if (!t) return;
      if (!eventTypes || eventTypes.length === 0 || eventTypes.includes(t)) {
        fetchData();
      }
    };
    window.addEventListener('tv:realtime', handler);
    return () => window.removeEventListener('tv:realtime', handler);
  }, [fetchData, eventTypes]);
};

const TvViewPage = () => {
  const { api } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // iter59: load station config when ?station=<id> is present, else use local config.
  const stationId = params.get('station') || null;
  const [station, setStation] = useState(null);
  useEffect(() => {
    let live = true;
    if (!stationId) { setStation(null); return; }
    (async () => {
      try {
        const r = await api().get(`/tv/stations/${stationId}`);
        if (!live) return;
        setStation(r.data);
        // Apply station config to local rotation + view
        if (r.data && Array.isArray(r.data.viewpoints) && r.data.viewpoints.length) {
          setRotation({
            enabled: r.data.viewpoints.length >= 2,
            viewpoints: r.data.viewpoints,
            interval: r.data.rotation_seconds || 30,
          });
          setView(r.data.viewpoints[0]);
        }
      } catch { setStation(null); }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  const initialView = params.get('view') || (typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null) || 'production';
  const [view, setView] = useState(initialView);

  // iter54: rotation mode
  const initialRotation = useMemo(() => {
    try {
      const raw = window.localStorage.getItem(ROTATION_KEY);
      if (!raw) return { enabled: false, viewpoints: [], interval: 30 };
      const parsed = JSON.parse(raw);
      return { enabled: !!parsed.enabled, viewpoints: parsed.viewpoints || [], interval: parsed.interval || 30 };
    } catch { return { enabled: false, viewpoints: [], interval: 30 }; }
  }, []);
  const [rotation, setRotation] = useState(initialRotation);
  const rotationTimerRef = useRef(null);

  const [now, setNow] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rotationOpen, setRotationOpen] = useState(false);

  // Persist view selection (URL + localStorage)
  useEffect(() => {
    if (params.get('view') !== view) {
      const next = new URLSearchParams(params);
      next.set('view', view);
      setParams(next, { replace: true });
    }
    try { window.localStorage.setItem(STORAGE_KEY, view); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Persist rotation config
  useEffect(() => {
    try { window.localStorage.setItem(ROTATION_KEY, JSON.stringify(rotation)); } catch {}
  }, [rotation]);

  // Rotation timer
  useEffect(() => {
    if (rotationTimerRef.current) {
      clearInterval(rotationTimerRef.current);
      rotationTimerRef.current = null;
    }
    if (rotation.enabled && rotation.viewpoints.length >= 2) {
      rotationTimerRef.current = setInterval(() => {
        setView((current) => {
          const list = rotation.viewpoints;
          const idx = list.indexOf(current);
          return list[(idx + 1) % list.length];
        });
      }, Math.max(5, rotation.interval) * 1000);
    }
    return () => {
      if (rotationTimerRef.current) clearInterval(rotationTimerRef.current);
    };
  }, [rotation]);

  useEffect(() => {
    const c = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(c);
  }, []);

  // iter78p — Weather in header (persistent across all viewpoints).
  // Was previously only shown on Company News / Inventory Pulse / Delivery
  // Schedule viewpoints — user reported it "no longer shows up on the TV
  // section" when on other viewpoints. Pin a compact chip in the header.
  const [headerWeather, setHeaderWeather] = useState(null);
  useEffect(() => {
    let alive = true;
    const fetchW = async () => {
      try {
        const r = await api().get('/tv/weather');
        if (alive) setHeaderWeather(r.data);
      } catch { /* silent */ }
    };
    fetchW();
    const i = setInterval(fetchW, 5 * 60_000);
    const handler = () => fetchW();
    window.addEventListener('tv:realtime', handler);
    return () => { alive = false; clearInterval(i); window.removeEventListener('tv:realtime', handler); };
  }, [api]);

  // iter78t — Birthday banner. Fetch today's celebrants (1×/hour) and pop a
  // floating overlay whenever the viewpoint changes (and once on first mount).
  const [todayBirthdays, setTodayBirthdays] = useState([]);
  const [birthdayBanner, setBirthdayBanner] = useState(null);
  useEffect(() => {
    let alive = true;
    const fetchCelebs = async () => {
      try {
        const r = await api().get('/tv/celebrations');
        if (!alive) return;
        const todays = (r.data?.birthdays || []).filter(b => b.is_today);
        setTodayBirthdays(todays);
      } catch { /* silent */ }
    };
    fetchCelebs();
    const i = setInterval(fetchCelebs, 60 * 60_000);
    return () => { alive = false; clearInterval(i); };
  }, [api]);
  // Pop the banner whenever the viewpoint changes — if anyone has a birthday today.
  useEffect(() => {
    if (!todayBirthdays.length) return;
    setBirthdayBanner(todayBirthdays);
    const t = setTimeout(() => setBirthdayBanner(null), 8000);
    return () => clearTimeout(t);
  }, [view, todayBirthdays]);


  // iter61 — Public TV WebSocket. Receives "tv:*" events from the backend so
  // viewpoints can refresh instantly (recall, qc fail, news, station, weather,
  // production-pace). The 60 s polling stays in place as a resilient fallback.
  useEffect(() => {
    let alive = true;
    let ws = null;
    let retryTimer = null;
    let pingTimer = null;
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${window.location.host}/api/ws/tv`;
    const connect = () => {
      if (!alive) return;
      try {
        ws = new WebSocket(url);
        ws.onopen = () => {
          pingTimer = setInterval(() => { try { ws.send('ping'); } catch (_) {} }, 25000);
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg && typeof msg.type === 'string' && msg.type.startsWith('tv:')) {
              window.dispatchEvent(new CustomEvent('tv:realtime', { detail: msg }));
            }
          } catch (_) { /* ignore non-json (e.g. "pong") */ }
        };
        ws.onclose = () => {
          if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
          if (alive) retryTimer = setTimeout(connect, 5000);
        };
        ws.onerror = () => { try { ws.close(); } catch (_) {} };
      } catch (_) {
        if (alive) retryTimer = setTimeout(connect, 5000);
      }
    };
    connect();
    return () => {
      alive = false;
      if (retryTimer) clearTimeout(retryTimer);
      if (pingTimer) clearInterval(pingTimer);
      if (ws) { try { ws.close(); } catch (_) {} }
    };
  }, []);

  // iter59: red-alert detector — flashes the screen + plays a chime when a
  // viewpoint transitions into a "red" state (recall opens, severe weather,
  // production behind, quality below threshold). Per-station audio/visual flags.
  const audioEnabled = station ? !!station.audio_alerts : true;
  const visualEnabled = station ? !!station.visual_alerts : true;
  const [alertActive, setAlertActive] = useState(false);
  const lastRedRef = useRef({});                    // viewpoint → was red
  const triggerAlert = useCallback((reason) => {
    if (!visualEnabled && !audioEnabled) return;
    if (visualEnabled) {
      setAlertActive(true);
      setTimeout(() => setAlertActive(false), 3500);
    }
    if (audioEnabled) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const playTone = (freq, when, dur) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = 'sine';
          o.frequency.value = freq;
          g.gain.setValueAtTime(0, ctx.currentTime + when);
          g.gain.linearRampToValueAtTime(0.4, ctx.currentTime + when + 0.02);
          g.gain.linearRampToValueAtTime(0, ctx.currentTime + when + dur);
          o.connect(g); g.connect(ctx.destination);
          o.start(ctx.currentTime + when);
          o.stop(ctx.currentTime + when + dur + 0.05);
        };
        // Two-tone alert (880 → 660 → 880)
        playTone(880, 0, 0.18);
        playTone(660, 0.22, 0.18);
        playTone(880, 0.44, 0.30);
        setTimeout(() => { try { ctx.close(); } catch {} }, 1200);
      } catch {}
    }
    // eslint-disable-next-line no-console
    console.log('[TV] red alert', reason);
  }, [audioEnabled, visualEnabled]);

  // Helper used by viewpoints — they call onRedStatus(true|false) when their
  // status changes; we trigger alert only on false → true edges.
  const onRedStatus = useCallback((vpKey, isRed) => {
    const wasRed = lastRedRef.current[vpKey] || false;
    lastRedRef.current[vpKey] = isRed;
    if (!wasRed && isRed) triggerAlert(vpKey);
  }, [triggerAlert]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch { /* ignore */ }
  };

  const toggleRotationViewpoint = (key) => {
    setRotation(r => {
      const has = r.viewpoints.includes(key);
      const list = has ? r.viewpoints.filter(v => v !== key) : [...r.viewpoints, key];
      return { ...r, viewpoints: list };
    });
  };

  const current = VIEWPOINTS.find(v => v.key === view) || VIEWPOINTS[0];
  const Icon = current.icon;

  const tvLogo = (branding?.show_logo_on_tv !== false)
    ? (branding?.tv_logo_data || branding?.logo_data || '')
    : '';
  const appName = branding?.app_name || 'BOM Maestro';
  const bannerImg = branding?.banner_data || '';
  const bannerText = branding?.banner_text || '';

  return (
    <div className="fixed inset-0 bg-slate-950 text-white overflow-hidden flex flex-col" data-testid="tv-view-page">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          {tvLogo ? (
            <div className="bg-white rounded-md py-2 px-3 inline-flex items-center" data-testid="tv-brand-logo-pill">
              <img src={tvLogo} alt={appName} className="h-12 max-w-[180px] object-contain" data-testid="tv-brand-logo" />
            </div>
          ) : (
            <Icon className="w-8 h-8" style={{ color: branding?.primary_color || '#f97316' }} />
          )}
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{current.label}</h1>
            <p className="text-sm text-slate-400 uppercase tracking-widest">
              {station ? <span className="text-orange-400 mr-2" data-testid="tv-station-name">{station.name} ·</span> : null}
              {current.description}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* iter78p — persistent compact weather chip (visible on all viewpoints) */}
          <HeaderWeatherChip weather={headerWeather} />
          <div className="text-right">
            <p className="text-3xl font-mono font-bold">{now.toLocaleTimeString()}</p>
            <p className="text-xs text-slate-400">{now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</p>
          </div>

          {/* Rotation mode picker */}
          <div className="relative">
            <button
              onClick={() => setRotationOpen(o => !o)}
              data-testid="tv-rotation-trigger"
              className={`px-3 py-2 text-xs rounded-sm flex items-center gap-1 ${rotation.enabled ? 'bg-orange-600 hover:bg-orange-500' : 'bg-slate-800 hover:bg-slate-700'}`}
              title="Rotation mode"
            >
              <Repeat className={`w-4 h-4 ${rotation.enabled ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">Rotate {rotation.enabled ? `· ${rotation.interval}s` : ''}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {rotationOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setRotationOpen(false)} />
                <div className="absolute right-0 mt-1 w-80 bg-slate-900 border border-slate-700 rounded-sm shadow-xl z-20 p-3" data-testid="tv-rotation-menu">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase tracking-wider text-slate-400">Auto-rotate viewpoints</span>
                    <button
                      onClick={() => setRotation(r => ({ ...r, enabled: !r.enabled }))}
                      data-testid="tv-rotation-toggle"
                      className={`relative w-9 h-5 rounded-full transition ${rotation.enabled ? 'bg-orange-500' : 'bg-slate-700'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition ${rotation.enabled ? 'translate-x-4' : ''}`} />
                    </button>
                  </div>
                  <div className="space-y-1 mb-3">
                    {VIEWPOINTS.map(v => {
                      const checked = rotation.viewpoints.includes(v.key);
                      return (
                        <button
                          key={v.key}
                          type="button"
                          onClick={() => toggleRotationViewpoint(v.key)}
                          data-testid={`tv-rotation-vp-${v.key}`}
                          className={`w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-sm hover:bg-slate-800 ${checked ? 'bg-slate-800/60' : ''}`}
                        >
                          <span className={`w-4 h-4 rounded-sm border ${checked ? 'bg-orange-500 border-orange-500' : 'border-slate-600'} flex items-center justify-center`}>
                            {checked && <Check className="w-3 h-3 text-white" />}
                          </span>
                          <span className="text-sm">{v.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-xs text-slate-400 mb-1">Interval</div>
                  <div className="flex flex-wrap gap-1">
                    {ROTATE_INTERVALS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setRotation(r => ({ ...r, interval: opt.value }))}
                        data-testid={`tv-rotation-interval-${opt.value}`}
                        className={`px-2 py-1 text-xs rounded-sm ${rotation.interval === opt.value ? 'bg-orange-500' : 'bg-slate-800 hover:bg-slate-700'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-3">
                    Pick 2+ viewpoints + an interval, then flip the switch. The TV will auto-cycle.
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Viewpoint picker */}
          <div className="relative">
            <button
              onClick={() => setPickerOpen(o => !o)}
              data-testid="tv-viewpoint-trigger"
              className="px-3 py-2 text-xs bg-slate-800 hover:bg-slate-700 rounded-sm flex items-center gap-1"
              title="Pick TV viewpoint"
            >
              <Tv className="w-4 h-4" /> View <ChevronDown className="w-3 h-3" />
            </button>
            {pickerOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setPickerOpen(false)} />
                <div className="absolute right-0 mt-1 w-72 bg-slate-900 border border-slate-700 rounded-sm shadow-xl z-20" data-testid="tv-viewpoint-menu">
                  {VIEWPOINTS.map(v => {
                    const Vi = v.icon;
                    const active = v.key === view;
                    return (
                      <button
                        key={v.key}
                        onClick={() => { setView(v.key); setPickerOpen(false); }}
                        data-testid={`tv-viewpoint-option-${v.key}`}
                        className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 hover:bg-slate-800 transition ${active ? 'bg-slate-800/70' : ''}`}
                      >
                        <Vi className={`w-4 h-4 mt-0.5 ${active ? 'text-orange-400' : 'text-slate-400'}`} />
                        <div>
                          <div className={`text-sm font-medium ${active ? 'text-orange-400' : 'text-white'}`}>{v.label}</div>
                          <div className="text-[11px] text-slate-400">{v.description}</div>
                        </div>
                        {active && <span className="ml-auto text-[10px] text-orange-400">●</span>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <button onClick={toggleFullscreen} className="px-3 py-2 text-xs bg-slate-800 hover:bg-slate-700 rounded-sm" data-testid="tv-fullscreen-btn">
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button onClick={() => navigate('/')} className="px-3 py-2 text-xs bg-slate-800 hover:bg-slate-700 rounded-sm" data-testid="tv-exit-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* iter54: Optional banner image / safety message */}
      {(bannerImg || bannerText) && (
        <div
          className="relative w-full overflow-hidden border-b border-slate-800"
          style={bannerImg ? { backgroundImage: `url(${bannerImg})`, backgroundSize: 'cover', backgroundPosition: 'center', minHeight: '64px' } : { minHeight: bannerText ? '40px' : '0' }}
          data-testid="tv-banner"
        >
          {bannerText && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 px-6 py-2">
              <p className="text-lg uppercase tracking-widest font-semibold text-white">{bannerText}</p>
            </div>
          )}
        </div>
      )}

      {/* Body */}
      {view === 'production' && <ProductionViewpoint api={api} initialPeriod={station?.default_period || params.get('period') || 'weekly'} setParams={setParams} params={params} stationPtId={station?.product_type_id || null} onRedStatus={onRedStatus} />}
      {view === 'open_orders' && <OpenOrdersViewpoint api={api} />}
      {view === 'inventory_pulse' && <InventoryPulseViewpoint api={api} />}
      {view === 'on_shift' && <OnShiftViewpoint api={api} />}
      {view === 'quality_pulse' && <QualityPulseViewpoint api={api} onRedStatus={onRedStatus} />}
      {view === 'finished_today' && <FinishedTodayViewpoint api={api} />}
      {view === 'repair_board' && <RepairBoardViewpoint api={api} refreshMs={REFRESH_MS} />}
      {view === 'delivery_schedule' && <DeliveryScheduleViewpoint api={api} />}
      {view === 'active_recalls' && <ActiveRecallsViewpoint api={api} onRedStatus={onRedStatus} />}
      {view === 'safety' && <SafetyViewpoint api={api} />}
      {view === 'celebrations' && <CelebrationsViewpoint api={api} />}
      {view === 'company_news' && <CompanyNewsViewpoint api={api} onRedStatus={onRedStatus} />}

      {/* iter78t — Birthday banner overlay (between viewpoint switches) */}
      {birthdayBanner && birthdayBanner.length > 0 && (
        <div
          className="fixed top-24 left-1/2 -translate-x-1/2 z-[55] px-8 py-5 rounded-sm shadow-2xl"
          style={{
            background: 'linear-gradient(135deg, #ec4899 0%, #f59e0b 100%)',
            border: '2px solid #fde68a',
            animation: 'fadeIn 400ms ease-out',
          }}
          data-testid="tv-birthday-banner"
        >
          <div className="flex items-center gap-4 text-white">
            <span className="text-5xl" role="img" aria-label="cake">🎂</span>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] opacity-90">Happy Birthday</p>
              <p className="font-bold text-3xl leading-tight" data-testid="tv-birthday-names">
                {birthdayBanner.map(b => b.name).join(' · ')}
              </p>
              <p className="text-xs opacity-90 mt-1">From the whole shop floor 🎉</p>
            </div>
          </div>
        </div>
      )}

      {/* iter59: visual red-alert flash overlay */}
      {alertActive && (
        <div
          className="fixed inset-0 pointer-events-none z-[60] animate-pulse"
          style={{ background: 'rgba(220,38,38,0.35)', boxShadow: 'inset 0 0 200px 60px rgba(220,38,38,0.9)' }}
          data-testid="tv-red-alert-overlay"
        />
      )}

      <div className="px-8 pb-3 text-xs text-slate-500 flex items-center justify-between">
        <span>Auto-refresh every 60 s · Viewpoint saved per-device{rotation.enabled && rotation.viewpoints.length > 1 ? ` · Rotating ${rotation.viewpoints.length} views every ${rotation.interval}s` : ''}</span>
        <span>Press F11 or click ⛶ for full-screen · ESC to exit</span>
      </div>
    </div>
  );
};

// =================== Production Floor (existing) ===================
const ProductionViewpoint = ({ api, initialPeriod, setParams, params, stationPtId, onRedStatus }) => {
  const [period, setPeriod] = useState(initialPeriod);
  const [progress, setProgress] = useState(null);
  const [productTypes, setProductTypes] = useState([]);
  const initialPt = stationPtId || params.get('pt') || (typeof window !== 'undefined' ? window.localStorage.getItem(PT_FILTER_KEY) : '') || '';
  const [productTypeId, setProductTypeId] = useState(initialPt);
  const [ptOpen, setPtOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const url = `/dashboard/production-progress?period_type=${period}${productTypeId ? `&product_type_id=${encodeURIComponent(productTypeId)}` : ''}`;
      const [pRes, ptRes] = await Promise.all([
        api().get(url),
        api().get('/product-types').catch(() => ({ data: [] })),
      ]);
      setProgress(pRes.data);
      setProductTypes(ptRes.data || []);
    } catch { /* silent */ }
  }, [api, period, productTypeId]);
  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_MS); return () => clearInterval(i); }, [fetchData]);

  // Persist pt filter
  useEffect(() => {
    try {
      if (productTypeId) window.localStorage.setItem(PT_FILTER_KEY, productTypeId);
      else window.localStorage.removeItem(PT_FILTER_KEY);
      const p = new URLSearchParams(params);
      if (productTypeId) p.set('pt', productTypeId); else p.delete('pt');
      setParams(p, { replace: true });
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productTypeId]);

  const cyclePeriod = () => {
    const idx = PERIODS.indexOf(period);
    const next = PERIODS[(idx + 1) % PERIODS.length];
    setPeriod(next);
    const p = new URLSearchParams(params); p.set('period', next); setParams(p, { replace: true });
  };

  const activePt = productTypes.find(p => p.id === productTypeId);
  const activePtName = activePt?.product_type_name || 'All Products';

  const win = progress?.window;
  const totals = progress?.totals || {};
  const completionRate = totals.completion_rate;
  const onPace = totals.delta_vs_pace !== null && totals.delta_vs_pace !== undefined && totals.delta_vs_pace >= 0;
  const overallPct = totals.target ? Math.min(100, Math.round((totals.completed / totals.target) * 100)) : 0;
  const expectedPct = totals.target ? Math.min(100, Math.round((totals.expected_pace / totals.target) * 100)) : 0;
  const dayMax = (progress?.day_series || []).reduce((m, x) => Math.max(m, x.completed || 0), 1);
  const status = (() => {
    if (totals.target === 0 || totals.target === undefined) return { tone: 'slate', label: 'NO TARGET' };
    if (completionRate >= 100) return { tone: 'green', label: 'TARGET HIT' };
    if (onPace) return { tone: 'green', label: 'ON PACE' };
    return { tone: 'red', label: 'BEHIND PACE' };
  })();
  const toneBg = { slate: 'bg-slate-700', green: 'bg-emerald-600', red: 'bg-red-600' }[status.tone];

  // iter59: notify red-status edge transitions
  useEffect(() => {
    if (onRedStatus) onRedStatus('production', status.tone === 'red');
  }, [status.tone, onRedStatus]);

  return (
    <div className="flex-1 grid grid-cols-12 gap-6 p-8 overflow-hidden">
      <div className="col-span-5 flex flex-col">
        <div className={`flex-1 ${toneBg} rounded-md p-8 flex flex-col justify-center items-center text-center`} data-testid="tv-status-card">
          <div className="flex items-center gap-2 mb-2">
            <button onClick={cyclePeriod} className="text-[11px] uppercase bg-black/30 px-2 py-0.5 rounded" data-testid="tv-cycle-period">{period} ↻</button>
            <div className="relative">
              <button
                onClick={() => setPtOpen(o => !o)}
                className="text-[11px] uppercase bg-black/30 px-2 py-0.5 rounded flex items-center gap-1"
                data-testid="tv-pt-filter-trigger"
                title="Filter by product type"
              >
                {activePtName} <ChevronDown className="w-3 h-3" />
              </button>
              {ptOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setPtOpen(false)} />
                  <div className="absolute left-0 mt-1 w-64 bg-slate-900 border border-slate-700 rounded-sm shadow-xl z-20 max-h-[60vh] overflow-y-auto" data-testid="tv-pt-filter-menu">
                    <button
                      onClick={() => { setProductTypeId(''); setPtOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800 ${!productTypeId ? 'bg-slate-800/70 text-orange-400' : 'text-white'}`}
                      data-testid="tv-pt-filter-all"
                    >
                      All Products
                    </button>
                    {productTypes.filter(p => p.active_status !== false).map(pt => (
                      <button
                        key={pt.id}
                        onClick={() => { setProductTypeId(pt.id); setPtOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800 ${productTypeId === pt.id ? 'bg-slate-800/70 text-orange-400' : 'text-white'}`}
                        data-testid={`tv-pt-filter-${pt.id}`}
                      >
                        {pt.product_type_name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <p className="text-2xl uppercase tracking-widest text-white/80 mb-4">{status.label}</p>
          <p className="font-mono font-black text-9xl leading-none" data-testid="tv-completed-value">{totals.completed ?? 0}</p>
          <p className="text-3xl mt-3 text-white/90">of {totals.target ?? '—'}</p>
          {completionRate !== null && completionRate !== undefined ? (
            <p className="mt-6 font-mono font-bold text-6xl" data-testid="tv-completion-rate">{Math.round(completionRate)}%</p>
          ) : (
            <p className="mt-6 font-mono font-bold text-3xl text-white/60" data-testid="tv-completion-rate">No target set</p>
          )}
          {totals.expected_pace !== null && totals.expected_pace !== undefined && (
            <p className="text-base text-white/80 mt-2">Expected pace: {totals.expected_pace.toFixed(1)} · {totals.delta_vs_pace >= 0 ? '+' : ''}{(totals.delta_vs_pace ?? 0).toFixed(1)}</p>
          )}
        </div>
        {win && (
          <div className="mt-3 bg-slate-900 rounded-sm p-3 text-center">
            <p className="text-xs text-slate-400 uppercase">Window</p>
            <p className="font-mono">{win.start} → {win.end}</p>
            <p className="text-xs text-slate-400 mt-1">Day {win.days_elapsed} / {win.days_in_window}</p>
          </div>
        )}
      </div>

      <div className="col-span-7 flex flex-col gap-4 overflow-hidden">
        <div className="bg-slate-900 rounded-md p-4">
          <div className="flex items-center justify-between text-sm mb-2 text-slate-400 uppercase tracking-wide">
            <span>Overall progress</span><span className="font-mono">{overallPct}%</span>
          </div>
          <div className="relative h-6 bg-slate-800 rounded-full overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-orange-500 transition-all" style={{ width: `${overallPct}%` }} />
            {expectedPct > 0 && <div className="absolute inset-y-0 w-1 bg-white/70" style={{ left: `${expectedPct}%` }} />}
          </div>
        </div>
        {(progress?.product_breakdown || []).length > 0 && (
          <div className="bg-slate-900 rounded-md p-4 flex-1 overflow-y-auto" data-testid="tv-product-breakdown">
            <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">By Product Type</p>
            <div className="space-y-2">
              {progress.product_breakdown.slice(0, 8).map(b => {
                const pct = b.target > 0 ? Math.min(100, Math.round((b.completed / b.target) * 100)) : 0;
                return (
                  <div key={b.product_type_id} data-testid={`tv-pt-${b.product_type_id}`}>
                    <div className="flex items-center justify-between text-base">
                      <span className="truncate max-w-[60%]">{b.product_type_name}</span>
                      <span className="font-mono">{b.completed}{b.target ? ` / ${b.target}` : ''}{b.target ? ` · ${pct}%` : ''}</span>
                    </div>
                    <div className="h-3 bg-slate-800 rounded-full overflow-hidden mt-1">
                      <div className={`h-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-orange-500'}`} style={{ width: `${pct || 0}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          {(progress?.day_series || []).length > 0 && (
            <div className="bg-slate-900 rounded-md p-4">
              <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Per Day</p>
              <div className="flex items-end gap-1 h-28" data-testid="tv-day-chart">
                {progress.day_series.map(d => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-orange-500 rounded-t-sm" style={{ height: `${(d.completed / dayMax) * 90 || 2}%` }} />
                    <span className="text-[10px] text-slate-400 mono">{d.date.slice(5)}</span>
                    <span className="text-xs font-mono">{d.completed}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(progress?.top_users || []).length > 0 && (
            <div className="bg-slate-900 rounded-md p-4">
              <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Top Assemblers</p>
              <ul className="space-y-1">
                {progress.top_users.map((u, idx) => (
                  <li key={u.name} className="flex items-center justify-between text-base" data-testid={`tv-top-${idx}`}>
                    <span className="truncate max-w-[70%]"><span className="text-slate-400 mr-2">#{idx + 1}</span>{u.name}</span>
                    <span className="font-mono font-bold text-orange-400">{u.completed}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// =================== Open Orders Viewpoint ===================
const OpenOrdersViewpoint = ({ api }) => {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const fetchData = useCallback(async () => {
    try {
      const [sRes, oRes] = await Promise.all([
        api().get('/expected-orders/summary'),
        api().get('/expected-orders'),
      ]);
      setSummary(sRes.data);
      setOrders((oRes.data || []).filter(o => o.status === 'pending'));
    } catch { /* silent */ }
  }, [api]);
  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_MS); return () => clearInterval(i); }, [fetchData]);

  const counts = summary?.counts || {};
  const overdueOrders = orders.filter(o => o.is_overdue);
  const tone = counts.overdue > 0 ? 'bg-red-600' : 'bg-emerald-600';
  const overdueLabel = counts.overdue > 0 ? `${counts.overdue} OVERDUE` : 'ALL ON TRACK';

  return (
    <div className="flex-1 grid grid-cols-12 gap-6 p-8 overflow-hidden" data-testid="tv-open-orders">
      <div className="col-span-5 flex flex-col gap-4">
        <div className={`flex-1 ${tone} rounded-md p-8 flex flex-col justify-center items-center text-center`}>
          <p className="text-2xl uppercase tracking-widest text-white/80 mb-3">{overdueLabel}</p>
          <p className="font-mono font-black text-9xl leading-none" data-testid="tv-pending-count">{counts.pending || 0}</p>
          <p className="text-3xl mt-3 text-white/90">orders pending</p>
          <p className="mt-6 font-mono font-bold text-5xl" data-testid="tv-pending-value">{fmt$(summary?.pending_value_usd || 0)}</p>
          <p className="text-base text-white/80 mt-1">value on order</p>
        </div>
        {summary?.avg_days_to_arrival != null && (
          <div className="bg-slate-900 rounded-md p-4 text-center">
            <p className="text-sm text-slate-400 uppercase tracking-wide">Avg Days to ETA</p>
            <p className="font-mono font-bold text-5xl mt-1">{Math.round(summary.avg_days_to_arrival)}d</p>
          </div>
        )}
      </div>

      <div className="col-span-7 flex flex-col gap-4 overflow-hidden">
        <div className="bg-slate-900 rounded-md p-4">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Top Vendors by Open $</p>
          <div className="space-y-2">
            {(summary?.top_vendors || []).length === 0 && <p className="text-slate-500 italic text-sm">No open orders.</p>}
            {(summary?.top_vendors || []).map((v, idx) => (
              <div key={v.vendor_id} className="flex items-center justify-between text-base" data-testid={`tv-top-vendor-${idx}`}>
                <span className="truncate max-w-[60%]"><span className="text-slate-400 mr-2">#{idx + 1}</span>{v.vendor_name}</span>
                <span className="font-mono">{v.open_count} · <span className="text-orange-400 font-bold">{fmt$(v.open_value_usd)}</span></span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-slate-900 rounded-md p-4 flex-1 overflow-y-auto">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">
            Overdue Lines{overdueOrders.length > 0 && <span className="text-red-400 ml-2">({overdueOrders.length})</span>}
          </p>
          {overdueOrders.length === 0 ? (
            <p className="text-slate-500 italic text-sm">Everything is on schedule. Great work.</p>
          ) : (
            <table className="w-full text-base">
              <thead className="text-[11px] text-slate-400 uppercase">
                <tr className="border-b border-slate-800">
                  <th className="text-left py-1">PO #</th>
                  <th className="text-left py-1">Item</th>
                  <th className="text-left py-1">Vendor</th>
                  <th className="text-right py-1">Qty</th>
                  <th className="text-right py-1">Days</th>
                </tr>
              </thead>
              <tbody>
                {overdueOrders.slice(0, 8).map(o => (
                  <tr key={o.id} className="border-b border-slate-800/40">
                    <td className="py-2 mono">{o.purchase_order_number || '—'}</td>
                    <td className="py-2 truncate max-w-[180px]">{o.item_sku}</td>
                    <td className="py-2 text-slate-400 truncate max-w-[160px]">{o.vendor_name || '—'}</td>
                    <td className="py-2 text-right mono">{o.expected_quantity}</td>
                    <td className="py-2 text-right text-red-400 font-bold mono">{Math.abs(o.days_until_arrival || 0)}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// =================== Inventory Pulse Viewpoint ===================
const InventoryPulseViewpoint = ({ api }) => {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState({ counts: {} });
  const fetchData = useCallback(async () => {
    try {
      const [iRes, sRes] = await Promise.all([
        api().get('/items'),
        api().get('/expected-orders/summary').catch(() => ({ data: { counts: {} } })),
      ]);
      setItems(iRes.data || []);
      setOrders(sRes.data || { counts: {} });
    } catch { /* silent */ }
  }, [api]);
  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_MS); return () => clearInterval(i); }, [fetchData]);

  const lowStock = items.filter(i => i.current_quantity <= (i.reorder_point || 0) && (i.reorder_point || 0) > 0);
  const oosCount = items.filter(i => i.current_quantity <= 0).length;
  const totalSkus = items.length;
  const tone = oosCount > 0 ? 'bg-red-600' : (lowStock.length > 0 ? 'bg-amber-600' : 'bg-emerald-600');
  const banner = oosCount > 0 ? `${oosCount} OUT OF STOCK` : (lowStock.length > 0 ? `${lowStock.length} LOW STOCK` : 'INVENTORY HEALTHY');

  return (
    <div className="flex-1 grid grid-cols-12 gap-6 p-8 overflow-hidden" data-testid="tv-inventory-pulse">
      <div className="col-span-5 flex flex-col gap-4">
        <div className={`flex-1 ${tone} rounded-md p-8 flex flex-col justify-center items-center text-center`}>
          <p className="text-2xl uppercase tracking-widest text-white/80 mb-3">{banner}</p>
          <p className="font-mono font-black text-9xl leading-none" data-testid="tv-low-stock-count">{lowStock.length}</p>
          <p className="text-3xl mt-3 text-white/90">items below reorder</p>
          <p className="mt-6 font-mono font-bold text-5xl text-white/90">{totalSkus}</p>
          <p className="text-base text-white/80 mt-1">SKUs tracked</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-900 rounded-md p-4 text-center">
            <p className="text-sm text-slate-400 uppercase tracking-wide">On Order</p>
            <p className="font-mono font-bold text-4xl mt-1">{orders?.counts?.pending || 0}</p>
          </div>
          <div className="bg-slate-900 rounded-md p-4 text-center">
            <p className="text-sm text-slate-400 uppercase tracking-wide">Overdue</p>
            <p className={`font-mono font-bold text-4xl mt-1 ${(orders?.counts?.overdue || 0) > 0 ? 'text-red-400' : ''}`}>{orders?.counts?.overdue || 0}</p>
          </div>
        </div>
      </div>

      <div className="col-span-7 flex flex-col gap-4 overflow-hidden">
        <div className="bg-slate-900 rounded-md p-4 flex-1 overflow-y-auto">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Items Below Reorder Point</p>
          {lowStock.length === 0 ? (
            <p className="text-slate-500 italic text-sm">Everything is above reorder. Solid stewardship.</p>
          ) : (
            <table className="w-full text-base">
              <thead className="text-[11px] text-slate-400 uppercase">
                <tr className="border-b border-slate-800">
                  <th className="text-left py-1">SKU</th>
                  <th className="text-left py-1">Item</th>
                  <th className="text-right py-1">On Hand</th>
                  <th className="text-right py-1">Reorder At</th>
                  <th className="text-right py-1">Gap</th>
                </tr>
              </thead>
              <tbody>
                {lowStock.slice(0, 12).map(i => {
                  const gap = (i.reorder_point || 0) - (i.current_quantity || 0);
                  const oos = (i.current_quantity || 0) <= 0;
                  return (
                    <tr key={i.id} className={`border-b border-slate-800/40 ${oos ? 'bg-red-950/30' : ''}`} data-testid={`tv-low-${i.id}`}>
                      <td className="py-2 mono">{i.item_sku}</td>
                      <td className="py-2 truncate max-w-[260px]">{i.item_name}</td>
                      <td className={`py-2 text-right mono font-bold ${oos ? 'text-red-400' : ''}`}>{i.current_quantity}</td>
                      <td className="py-2 text-right mono text-slate-400">{i.reorder_point}</td>
                      <td className="py-2 text-right mono text-orange-400">{gap}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// =================== On Shift Now ===================
const OnShiftViewpoint = ({ api }) => {
  const [data, setData] = useState(null);
  const fetchData = useCallback(async () => {
    try { const r = await api().get('/tv/on-shift-now'); setData(r.data); } catch {}
  }, [api]);
  useEffect(() => { fetchData(); const i = setInterval(fetchData, 30_000); return () => clearInterval(i); }, [fetchData]);

  const onShift = data?.on_shift || [];
  const tone = onShift.length === 0 ? 'bg-slate-700' : 'bg-emerald-600';
  return (
    <div className="flex-1 grid grid-cols-12 gap-6 p-8 overflow-hidden" data-testid="tv-on-shift">
      <div className="col-span-5 flex flex-col gap-4">
        <div className={`flex-1 ${tone} rounded-md p-8 flex flex-col justify-center items-center text-center`}>
          <p className="text-2xl uppercase tracking-widest text-white/80 mb-3">On Shift Right Now</p>
          <p className="font-mono font-black text-9xl leading-none" data-testid="tv-on-shift-count">{data?.on_shift_count ?? 0}</p>
          <p className="text-3xl mt-3 text-white/90">people clocked in</p>
          <p className="mt-6 font-mono font-bold text-5xl">{data?.today_total_hours ?? 0}h</p>
          <p className="text-base text-white/80 mt-1">total hours logged today</p>
        </div>
        <div className="bg-slate-900 rounded-md p-4">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Today's Top 5 by Hours</p>
          <ul className="space-y-1">
            {(data?.top_today || []).length === 0 && <p className="text-slate-500 italic text-sm">No closed entries yet today.</p>}
            {(data?.top_today || []).map((u, i) => (
              <li key={u.name + i} className="flex items-center justify-between text-base">
                <span><span className="text-slate-400 mr-2">#{i + 1}</span>{u.name}</span>
                <span className="font-mono font-bold text-orange-400">{u.hours}h</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="col-span-7 bg-slate-900 rounded-md p-4 overflow-y-auto">
        <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Currently Clocked In ({onShift.length})</p>
        {onShift.length === 0 ? (
          <p className="text-slate-500 italic">Nobody is clocked in right now.</p>
        ) : (
          <table className="w-full text-base">
            <thead className="text-[11px] text-slate-400 uppercase">
              <tr className="border-b border-slate-800">
                <th className="text-left py-1">Name</th>
                <th className="text-left py-1">Activity</th>
                <th className="text-right py-1">Elapsed</th>
              </tr>
            </thead>
            <tbody>
              {onShift.map(s => {
                const h = Math.floor(s.elapsed_minutes / 60);
                const m = s.elapsed_minutes % 60;
                const long = s.elapsed_minutes >= 480; // >8h
                return (
                  <tr key={`${s.user_id}-${s.clock_in}`} className={`border-b border-slate-800/40 ${long ? 'bg-amber-950/30' : ''}`} data-testid={`tv-on-shift-row-${s.user_id}`}>
                    <td className="py-2 font-medium">{s.display_name}</td>
                    <td className="py-2 text-slate-300 capitalize">{s.operation_name || s.project_label || s.category.replaceAll('_', ' ')}</td>
                    <td className={`py-2 text-right mono font-bold ${long ? 'text-amber-400' : 'text-emerald-400'}`}>{h}h {m.toString().padStart(2, '0')}m</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// =================== Quality Pulse ===================
const QualityPulseViewpoint = ({ api, onRedStatus }) => {
  const [data, setData] = useState(null);
  const fetchData = useCallback(async () => {
    try { const r = await api().get('/tv/quality-pulse'); setData(r.data); } catch {}
  }, [api]);
  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_MS); return () => clearInterval(i); }, [fetchData]);
  useTvRealtime(fetchData, ['tv:qc_fail']);

  const today = data?.today || {};
  const week = data?.week || {};
  const rate = week.pass_rate;
  const tone = rate === null || rate === undefined ? 'bg-slate-700' : (rate >= 95 ? 'bg-emerald-600' : rate >= 85 ? 'bg-amber-600' : 'bg-red-600');
  const banner = rate === null || rate === undefined ? 'NO QC DATA' : (rate >= 95 ? 'EXCELLENT' : rate >= 85 ? 'WATCH' : 'BELOW TARGET');
  const dayMax = (data?.day_series || []).reduce((m, d) => Math.max(m, (d.pass + d.fail) || 0), 1);
  const isRed = rate !== null && rate !== undefined && rate < 85;
  useEffect(() => { if (onRedStatus) onRedStatus('quality_pulse', isRed); }, [isRed, onRedStatus]);
  return (
    <div className="flex-1 grid grid-cols-12 gap-6 p-8 overflow-hidden" data-testid="tv-quality-pulse">
      <div className="col-span-5 flex flex-col gap-4">
        <div className={`flex-1 ${tone} rounded-md p-8 flex flex-col justify-center items-center text-center`}>
          <p className="text-2xl uppercase tracking-widest text-white/80 mb-3">{banner}</p>
          <p className="font-mono font-black text-9xl leading-none" data-testid="tv-week-pass-rate">{rate != null ? `${rate}%` : '—'}</p>
          <p className="text-3xl mt-3 text-white/90">7-day pass rate</p>
          <p className="mt-6 font-mono font-bold text-3xl">{week.passed || 0} pass · <span className="text-red-200">{week.failed || 0} fail</span></p>
          <p className="text-sm text-white/80 mt-1">{week.total || 0} inspections this week</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-900 rounded-md p-3 text-center">
            <p className="text-xs text-slate-400 uppercase">Today Pass</p>
            <p className="font-mono font-bold text-3xl text-emerald-400 mt-1">{today.passed || 0}</p>
          </div>
          <div className="bg-slate-900 rounded-md p-3 text-center">
            <p className="text-xs text-slate-400 uppercase">Today Fail</p>
            <p className="font-mono font-bold text-3xl text-red-400 mt-1">{today.failed || 0}</p>
          </div>
          <div className="bg-slate-900 rounded-md p-3 text-center">
            <p className="text-xs text-slate-400 uppercase">Today Rate</p>
            <p className="font-mono font-bold text-3xl mt-1">{today.pass_rate != null ? `${today.pass_rate}%` : '—'}</p>
          </div>
        </div>
      </div>
      <div className="col-span-7 flex flex-col gap-4 overflow-hidden">
        <div className="bg-slate-900 rounded-md p-4">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Inspections / Day (last 7)</p>
          <div className="flex items-end gap-1 h-24" data-testid="tv-qc-day-chart">
            {(data?.day_series || []).map(d => {
              const total = d.pass + d.fail;
              const passH = (d.pass / dayMax) * 90 || 0;
              const failH = (d.fail / dayMax) * 90 || 0;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex flex-col" style={{ height: '90%' }}>
                    <div className="flex-1" />
                    {d.fail > 0 && <div className="bg-red-500 w-full" style={{ height: `${failH}%` }} />}
                    {d.pass > 0 && <div className="bg-emerald-500 w-full" style={{ height: `${passH}%` }} />}
                  </div>
                  <span className="text-[10px] text-slate-400 mono">{d.date.slice(5)}</span>
                  <span className="text-xs font-mono">{total}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="bg-slate-900 rounded-md p-4">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Top Failing Checkpoints (7d)</p>
          {(data?.top_fails || []).length === 0 ? (
            <p className="text-slate-500 italic text-sm">No failed checks this week.</p>
          ) : (
            <ul className="space-y-2">
              {data.top_fails.map((f, i) => (
                <li key={f.label + i} className="flex items-center justify-between text-base">
                  <span className="truncate max-w-[70%]"><span className="text-slate-400 mr-2">#{i + 1}</span>{f.label}</span>
                  <span className="font-mono font-bold text-red-400">{f.fails}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-slate-900 rounded-md p-4 flex-1 overflow-y-auto">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Recent Failed Inspections</p>
          {(data?.recent_fails || []).length === 0 ? (
            <p className="text-slate-500 italic text-sm">Clean sheet — no failures this week.</p>
          ) : (
            <ul className="space-y-1">
              {data.recent_fails.map(f => (
                <li key={f.id} className="flex items-center justify-between text-sm border-b border-slate-800/40 py-1.5">
                  <span><span className="font-mono text-orange-400 mr-2">{f.serial}</span>{f.template}</span>
                  <span className="text-slate-400">{f.inspector} · {(f.submitted_at || '').slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// =================== Finished Today ===================
const FinishedTodayViewpoint = ({ api }) => {
  const [data, setData] = useState(null);
  const fetchData = useCallback(async () => {
    try { const r = await api().get('/tv/finished-today'); setData(r.data); } catch {}
  }, [api]);
  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_MS); return () => clearInterval(i); }, [fetchData]);

  const tone = (data?.today_count || 0) > 0 ? 'bg-emerald-600' : 'bg-slate-700';
  return (
    <div className="flex-1 grid grid-cols-12 gap-6 p-8 overflow-hidden" data-testid="tv-finished-today">
      <div className="col-span-5 flex flex-col gap-4">
        <div className={`flex-1 ${tone} rounded-md p-8 flex flex-col justify-center items-center text-center`}>
          <p className="text-2xl uppercase tracking-widest text-white/80 mb-3">Finished Today</p>
          <p className="font-mono font-black text-9xl leading-none" data-testid="tv-finished-today-count">{data?.today_count ?? 0}</p>
          <p className="text-3xl mt-3 text-white/90">units built today</p>
          <p className="mt-6 font-mono font-bold text-5xl">{data?.week_count ?? 0}</p>
          <p className="text-base text-white/80 mt-1">this week</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-900 rounded-md p-3 text-center">
            <p className="text-xs text-slate-400 uppercase">Final Products</p>
            <p className="font-mono font-bold text-4xl text-orange-400 mt-1">{data?.today_finals ?? 0}</p>
          </div>
          <div className="bg-slate-900 rounded-md p-3 text-center">
            <p className="text-xs text-slate-400 uppercase">Part Assemblies</p>
            <p className="font-mono font-bold text-4xl mt-1">{data?.today_parts ?? 0}</p>
          </div>
        </div>
      </div>
      <div className="col-span-7 flex flex-col gap-4 overflow-hidden">
        <div className="bg-slate-900 rounded-md p-4">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">By Product Type</p>
          {(data?.breakdown || []).length === 0 ? (
            <p className="text-slate-500 italic text-sm">Nothing built yet today.</p>
          ) : (
            <ul className="space-y-1">
              {data.breakdown.map(b => (
                <li key={b.product_type_id} className="flex items-center justify-between text-base">
                  <span className="truncate max-w-[60%]">{b.product_type_name}</span>
                  <span className="font-mono">today <span className="text-orange-400 font-bold">{b.today}</span> · week {b.week}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-slate-900 rounded-md p-4">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Top Assemblers Today</p>
          {(data?.top_assemblers || []).length === 0 ? (
            <p className="text-slate-500 italic text-sm">Waiting for the first build.</p>
          ) : (
            <ul className="space-y-1">
              {data.top_assemblers.map((u, i) => (
                <li key={u.name + i} className="flex items-center justify-between text-base">
                  <span><span className="text-slate-400 mr-2">#{i + 1}</span>{u.name}</span>
                  <span className="font-mono font-bold text-orange-400">{u.today}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-slate-900 rounded-md p-4 flex-1 overflow-y-auto">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Latest Serials</p>
          {(data?.latest || []).length === 0 ? (
            <p className="text-slate-500 italic text-sm">No builds yet.</p>
          ) : (
            <ul className="text-sm space-y-1">
              {data.latest.map(u => (
                <li key={u.serial} className="flex items-center justify-between border-b border-slate-800/40 py-1.5">
                  <span><span className="font-mono text-emerald-400 mr-2">{u.serial}</span>{u.product_type_name}</span>
                  <span className="text-slate-400">{u.built_by}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// =================== Delivery Schedule ===================
const DeliveryScheduleViewpoint = ({ api }) => {
  const [data, setData] = useState(null);
  const [weather, setWeather] = useState(null);
  const fetchData = useCallback(async () => {
    try { const r = await api().get('/tv/delivery-schedule'); setData(r.data); } catch {}
  }, [api]);
  const fetchWeather = useCallback(async () => {
    try { const r = await api().get('/tv/weather'); setWeather(r.data); } catch {}
  }, [api]);
  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_MS); return () => clearInterval(i); }, [fetchData]);
  useEffect(() => { fetchWeather(); const i = setInterval(fetchWeather, 5 * 60_000); return () => clearInterval(i); }, [fetchWeather]);

  const tone = (data?.drafts_count || 0) > 0 ? 'bg-amber-600' : 'bg-emerald-600';
  const showWeather = weather && weather.enabled !== false && weather.current;
  return (
    <div className="flex-1 grid grid-cols-12 gap-6 p-8 overflow-hidden" data-testid="tv-delivery-schedule">
      <div className="col-span-5 flex flex-col gap-4">
        <div className={`flex-1 ${tone} rounded-md p-8 flex flex-col justify-center items-center text-center`}>
          <p className="text-2xl uppercase tracking-widest text-white/80 mb-3">Shipping Today</p>
          <p className="font-mono font-black text-9xl leading-none" data-testid="tv-shipped-today-count">{data?.today_issued_count ?? 0}</p>
          <p className="text-3xl mt-3 text-white/90">BoLs issued today</p>
          <p className="mt-6 font-mono font-bold text-5xl">{data?.shipped_units_week ?? 0}</p>
          <p className="text-base text-white/80 mt-1">units shipped this week</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-900 rounded-md p-3 text-center">
            <p className="text-xs text-slate-400 uppercase">Drafts in Queue</p>
            <p className={`font-mono font-bold text-4xl mt-1 ${(data?.drafts_count || 0) > 0 ? 'text-amber-400' : ''}`}>{data?.drafts_count ?? 0}</p>
          </div>
          <div className="bg-slate-900 rounded-md p-3 text-center">
            <p className="text-xs text-slate-400 uppercase">This Week Issued</p>
            <p className="font-mono font-bold text-4xl text-orange-400 mt-1">{data?.week_issued_count ?? 0}</p>
          </div>
        </div>
      </div>
      <div className={`${showWeather ? 'col-span-4' : 'col-span-7'} flex flex-col gap-4 overflow-hidden`}>
        <div className="bg-slate-900 rounded-md p-4">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Top Consignees (30d)</p>
          {(data?.top_consignees || []).length === 0 ? (
            <p className="text-slate-500 italic text-sm">No shipments in the last 30 days.</p>
          ) : (
            <ul className="space-y-1">
              {data.top_consignees.map((c, i) => (
                <li key={c.name + i} className="flex items-center justify-between text-base">
                  <span><span className="text-slate-400 mr-2">#{i + 1}</span>{c.name}</span>
                  <span className="font-mono font-bold text-orange-400">{c.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-slate-900 rounded-md p-4 flex-1 overflow-y-auto">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Drafts in Queue</p>
          {(data?.drafts || []).length === 0 ? (
            <p className="text-slate-500 italic text-sm">No drafts pending.</p>
          ) : (
            <table className="w-full text-base">
              <thead className="text-[11px] text-slate-400 uppercase">
                <tr className="border-b border-slate-800">
                  <th className="text-left py-1">BoL</th>
                  <th className="text-left py-1">Consignee</th>
                  <th className="text-left py-1">Carrier</th>
                  <th className="text-right py-1">Lines</th>
                </tr>
              </thead>
              <tbody>
                {data.drafts.map((d, i) => (
                  <tr key={d.bol_number + i} className="border-b border-slate-800/40">
                    <td className="py-2 mono">{d.bol_number}</td>
                    <td className="py-2 truncate max-w-[200px]">{d.consignee}</td>
                    <td className="py-2 text-slate-400">{d.carrier}</td>
                    <td className="py-2 text-right mono">{d.items}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {showWeather && <div className="col-span-3"><WeatherPanel weather={weather} /></div>}
    </div>
  );
};

// =================== Active Recalls ===================
const ActiveRecallsViewpoint = ({ api, onRedStatus }) => {
  const [data, setData] = useState(null);
  const [weather, setWeather] = useState(null);
  const fetchData = useCallback(async () => {
    try { const r = await api().get('/tv/active-recalls'); setData(r.data); } catch {}
  }, [api]);
  const fetchWeather = useCallback(async () => {
    try { const r = await api().get('/tv/weather'); setWeather(r.data); } catch {}
  }, [api]);
  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_MS); return () => clearInterval(i); }, [fetchData]);
  useEffect(() => { fetchWeather(); const i = setInterval(fetchWeather, 5 * 60_000); return () => clearInterval(i); }, [fetchWeather]);
  useTvRealtime(fetchData, ['tv:recall']);
  useTvRealtime(fetchWeather, ['tv:weather']);

  const cnt = data?.active_count ?? 0;
  const tone = cnt > 0 ? 'bg-red-600 animate-pulse' : 'bg-emerald-600';
  const banner = cnt > 0 ? 'ACTIVE RECALLS' : 'NO ACTIVE RECALLS';
  const showWeather = weather && weather.enabled !== false && weather.current;
  // iter59: edge-trigger red alert when first recall opens
  useEffect(() => { if (onRedStatus) onRedStatus('active_recalls', cnt > 0); }, [cnt, onRedStatus]);
  return (
    <div className="flex-1 grid grid-cols-12 gap-6 p-8 overflow-hidden" data-testid="tv-active-recalls">
      <div className="col-span-5 flex flex-col gap-4">
        <div className={`flex-1 ${tone} rounded-md p-8 flex flex-col justify-center items-center text-center`}>
          <p className="text-2xl uppercase tracking-widest text-white/80 mb-3">{banner}</p>
          <p className="font-mono font-black text-9xl leading-none" data-testid="tv-active-recalls-count">{cnt}</p>
          <p className="text-3xl mt-3 text-white/90">open recall(s)</p>
          <p className="mt-6 font-mono font-bold text-5xl">{data?.affected_units_total ?? 0}</p>
          <p className="text-base text-white/80 mt-1">unit(s) affected</p>
        </div>
      </div>
      <div className={`${showWeather ? 'col-span-4' : 'col-span-7'} bg-slate-900 rounded-md p-4 overflow-y-auto`}>
        <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Open Recalls</p>
        {(data?.recalls || []).length === 0 ? (
          <p className="text-emerald-400 italic">All clear. No active recalls — keep it that way.</p>
        ) : (
          <ul className="space-y-2">
            {data.recalls.map(r => (
              <li key={r.id} className="bg-slate-950/40 border border-red-900/40 rounded-sm p-3" data-testid={`tv-recall-${r.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-red-400 font-bold">LOT {r.lot}</span>
                  <span className="text-xs text-slate-400">{(r.created_at || '').slice(0, 10)}</span>
                </div>
                <p className="text-sm mt-1">{r.reason}</p>
                <div className="flex items-center justify-between mt-2 text-xs text-slate-400">
                  <span>SKUs: {(r.affected_skus || []).slice(0, 4).join(', ') || '—'}</span>
                  <span className="text-orange-400 font-bold">{r.affected_units} unit(s)</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {showWeather && <div className="col-span-3"><WeatherPanel weather={weather} /></div>}
    </div>
  );
};

// =================== Safety ===================
const SafetyViewpoint = ({ api }) => {
  const [data, setData] = useState(null);
  const fetchData = useCallback(async () => {
    try { const r = await api().get('/tv/safety'); setData(r.data); } catch {}
  }, [api]);
  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_MS); return () => clearInterval(i); }, [fetchData]);

  const days = data?.days_without_incident;
  const isRecord = data?.is_record;
  const tone = days == null ? 'bg-slate-700' : (days >= (data?.record_days || 0) ? 'bg-emerald-600' : days >= 30 ? 'bg-amber-600' : 'bg-red-600');
  return (
    <div className="flex-1 flex items-center justify-center p-8" data-testid="tv-safety">
      <div className={`w-full max-w-5xl ${tone} rounded-md p-12 text-center shadow-2xl`}>
        <HardHat className="w-16 h-16 mx-auto mb-4 text-white/90" />
        <p className="text-2xl uppercase tracking-widest text-white/80 mb-4">Days Without a Safety Incident</p>
        <p className="font-mono font-black leading-none" style={{ fontSize: 'clamp(8rem, 22vw, 18rem)' }} data-testid="tv-safety-days">{days != null ? days : '—'}</p>
        <p className="text-3xl mt-6 text-white/90">{isRecord ? 'NEW RECORD · Stay vigilant' : `Record: ${data?.record_days ?? 0} days`}</p>
        {data?.last_incident_date && (
          <p className="text-sm text-white/70 mt-3">Last reset: {data.last_incident_date}</p>
        )}
        {data?.incident_note && (
          <p className="text-sm text-white/80 mt-2 italic">"{data.incident_note}"</p>
        )}
      </div>
    </div>
  );
};

// =================== Celebrations ===================
const CelebrationsViewpoint = ({ api }) => {
  const [data, setData] = useState(null);
  const fetchData = useCallback(async () => {
    try { const r = await api().get('/tv/celebrations'); setData(r.data); } catch {}
  }, [api]);
  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_MS); return () => clearInterval(i); }, [fetchData]);

  const todayCnt = data?.today_count || 0;
  const tone = todayCnt > 0 ? 'bg-fuchsia-600' : 'bg-slate-700';
  return (
    <div className="flex-1 grid grid-cols-12 gap-6 p-8 overflow-hidden" data-testid="tv-celebrations">
      <div className="col-span-5 flex flex-col gap-4">
        <div className={`flex-1 ${tone} rounded-md p-8 flex flex-col justify-center items-center text-center`}>
          <PartyPopper className="w-16 h-16 mb-3 text-white/90" />
          <p className="text-2xl uppercase tracking-widest text-white/80 mb-3">{todayCnt > 0 ? 'Celebrate Today!' : 'Coming Up'}</p>
          <p className="font-mono font-black text-9xl leading-none">{todayCnt}</p>
          <p className="text-3xl mt-3 text-white/90">today</p>
        </div>
      </div>
      <div className="col-span-7 grid grid-rows-2 gap-4 overflow-hidden">
        <div className="bg-slate-900 rounded-md p-4 overflow-y-auto">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Birthdays (next 14 days)</p>
          {(data?.birthdays || []).length === 0 ? (
            <p className="text-slate-500 italic text-sm">None coming up. Add birthdays in user profiles.</p>
          ) : (
            <ul className="space-y-1">
              {data.birthdays.map((b, i) => (
                <li key={b.name + i} className={`flex items-center justify-between text-base px-2 py-1.5 rounded-sm ${b.is_today ? 'bg-fuchsia-950/40' : ''}`}>
                  <span>{b.is_today ? '🎂 ' : ''}{b.name}</span>
                  <span className="text-slate-400 text-sm mono">{b.is_today ? 'TODAY' : `in ${b.days_until}d · ${b.date.slice(5)}`}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-slate-900 rounded-md p-4 overflow-y-auto">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-2">Work Anniversaries (next 14 days)</p>
          {(data?.anniversaries || []).length === 0 ? (
            <p className="text-slate-500 italic text-sm">None coming up. Add hire dates in user profiles.</p>
          ) : (
            <ul className="space-y-1">
              {data.anniversaries.map((a, i) => (
                <li key={a.name + i} className={`flex items-center justify-between text-base px-2 py-1.5 rounded-sm ${a.is_today ? 'bg-amber-950/40' : ''}`}>
                  <span>{a.is_today ? '🎉 ' : ''}{a.name} · <span className="text-orange-400 font-bold">{a.years} yr{a.years > 1 ? 's' : ''}</span></span>
                  <span className="text-slate-400 text-sm mono">{a.is_today ? 'TODAY' : `in ${a.days_until}d`}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// =================== Company News ===================
const COMPANY_NEWS_SLIDE_MS = 12_000;
const CompanyNewsViewpoint = ({ api, onRedStatus }) => {
  const [news, setNews] = useState([]);
  const [weather, setWeather] = useState(null);
  const [idx, setIdx] = useState(0);
  const fetchData = useCallback(async () => {
    try { const r = await api().get('/tv/company-news'); setNews(r.data?.news || []); } catch {}
  }, [api]);
  const fetchWeather = useCallback(async () => {
    try { const r = await api().get('/tv/weather'); setWeather(r.data); } catch {}
  }, [api]);
  useEffect(() => { fetchData(); const i = setInterval(fetchData, REFRESH_MS); return () => clearInterval(i); }, [fetchData]);
  useEffect(() => { fetchWeather(); const i = setInterval(fetchWeather, 5 * 60_000); return () => clearInterval(i); }, [fetchWeather]);
  useTvRealtime(fetchData, ['tv:news']);
  useTvRealtime(fetchWeather, ['tv:weather']);

  // iter59: edge-trigger red alert on severe weather
  const hasAlert = !!(weather && weather.enabled !== false && weather.has_alert);
  useEffect(() => { if (onRedStatus) onRedStatus('severe_weather', hasAlert); }, [hasAlert, onRedStatus]);

  // Auto-advance slide
  useEffect(() => {
    if (news.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % news.length), COMPANY_NEWS_SLIDE_MS);
    return () => clearInterval(t);
  }, [news.length]);

  const showWeather = weather && weather.enabled !== false && weather.current;

  if (!news.length) {
    return (
      <div className="flex-1 flex p-8 overflow-hidden gap-6" data-testid="tv-company-news-empty">
        <div className="flex-1 flex items-center justify-center text-center">
          <div>
            <Megaphone className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <p className="text-2xl text-slate-400">No company news yet.</p>
            <p className="text-sm text-slate-500 mt-2">Admins can post announcements in Settings → TV Display.</p>
          </div>
        </div>
        {showWeather && <WeatherPanel weather={weather} />}
      </div>
    );
  }
  const current = news[idx % news.length];
  return (
    <div className="flex-1 p-8 overflow-hidden flex gap-6" data-testid="tv-company-news">
      <div className="flex-1 bg-slate-900 rounded-md overflow-hidden flex flex-col">
        {current.image_data && (
          <div className="w-full h-1/2 overflow-hidden flex items-center justify-center bg-black">
            <img src={current.image_data} alt={current.title} className="max-h-full max-w-full object-contain" />
          </div>
        )}
        <div className={`${current.image_data ? 'h-1/2' : 'flex-1'} p-10 overflow-y-auto`}>
          <div className="flex items-center gap-3 mb-3 text-orange-400 uppercase tracking-widest text-sm">
            <Megaphone className="w-5 h-5" />
            {current.pinned ? 'PINNED · ' : ''}Company News
          </div>
          <h2 className="text-5xl font-bold mb-4 leading-tight">{current.title}</h2>
          <div className="text-xl text-slate-200 prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: current.body || '' }} />
          <p className="text-xs text-slate-500 mt-6">{(current.created_at || '').slice(0, 10)} · {current.created_by_name || ''}</p>
        </div>
        {news.length > 1 && (
          <div className="flex gap-1 px-4 pb-3">
            {news.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i === idx % news.length ? 'bg-orange-500' : 'bg-slate-800'}`} />
            ))}
          </div>
        )}
      </div>
      {showWeather && <WeatherPanel weather={weather} />}
    </div>
  );
};

// iter78p — Compact weather chip pinned in the TV header (visible on all viewpoints)
const HeaderWeatherChip = ({ weather }) => {
  if (!weather || weather.enabled === false || !weather.current) return null;
  const cur = weather.current;
  const tempUnit = weather.units === 'celsius' ? '°C' : '°F';
  const hasAlert = !!weather.has_alert;
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-sm border ${hasAlert ? 'bg-red-900/40 border-red-700' : 'bg-slate-900 border-slate-800'}`}
      data-testid="tv-header-weather"
      title={`${weather.location_label || 'Local weather'} — ${cur.label}`}
    >
      <span className="text-3xl leading-none">{WEATHER_ICON_MAP[cur.icon] || '☁'}</span>
      <div className="leading-tight">
        <div className="font-mono font-bold text-2xl" data-testid="tv-header-weather-temp">
          {Math.round(cur.temperature)}{tempUnit}
        </div>
        <div className={`text-[10px] uppercase tracking-widest ${hasAlert ? 'text-red-300' : 'text-slate-400'}`}>
          {hasAlert ? 'Severe' : (cur.label || '').slice(0, 14)}
        </div>
      </div>
    </div>
  );
};

// Reusable weather sidebar — used by Company News viewpoint
const WEATHER_ICON_MAP = {
  'sun': '☀',
  'cloud-sun': '⛅',
  'cloud': '☁',
  'cloud-fog': '🌫',
  'cloud-drizzle': '🌧',
  'cloud-rain': '🌧',
  'snowflake': '❄',
  'cloud-lightning': '⛈',
};
const WeatherPanel = ({ weather }) => {
  const tempUnit = weather.units === 'celsius' ? '°C' : '°F';
  const hasAlert = weather.has_alert;
  const cur = weather.current || {};
  return (
    <div className={`w-80 rounded-md flex flex-col overflow-hidden ${hasAlert ? 'bg-red-900/40 border border-red-700' : 'bg-slate-900'}`} data-testid="tv-weather-panel">
      <div className="p-4 border-b border-slate-800">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-widest text-slate-400">Local Weather</span>
          <span className="text-xs text-slate-500">{weather.location_label}</span>
        </div>
        {hasAlert && (
          <div className="mt-2 text-xs uppercase tracking-widest text-red-200 font-bold">⚠ SEVERE WEATHER ALERT</div>
        )}
        <div className="flex items-center gap-3 mt-3">
          <div className="text-6xl leading-none">{WEATHER_ICON_MAP[cur.icon] || '☁'}</div>
          <div>
            <div className="font-mono font-black text-5xl leading-none" data-testid="tv-weather-temp">{Math.round(cur.temperature)}{tempUnit}</div>
            <div className="text-sm text-slate-300 mt-1">{cur.label}</div>
            <div className="text-xs text-slate-500">Wind {Math.round(cur.wind_speed || 0)} {weather.wind_units}</div>
          </div>
        </div>
      </div>
      <div className="flex-1 p-3 overflow-y-auto">
        <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">7-Day Forecast</p>
        <div className="space-y-1.5">
          {(weather.daily || []).map((d, i) => {
            const date = new Date(d.date + 'T12:00:00');
            const day = i === 0 ? 'Today' : date.toLocaleDateString(undefined, { weekday: 'short' });
            return (
              <div key={d.date} className="flex items-center gap-2 text-sm" data-testid={`tv-weather-day-${i}`}>
                <span className="w-12 text-slate-300">{day}</span>
                <span className="w-6 text-center text-lg">{WEATHER_ICON_MAP[d.icon] || '·'}</span>
                <span className="flex-1 text-xs text-slate-400 truncate">{d.label}</span>
                <span className="font-mono text-xs">
                  <span className="text-orange-400 font-bold">{Math.round(d.high)}°</span>
                  <span className="text-slate-500"> / {Math.round(d.low)}°</span>
                </span>
                {d.precip_prob > 30 && (
                  <span className="font-mono text-[10px] text-blue-400">{d.precip_prob}%</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TvViewPage;
